import React from 'react'
import styled from 'styled-components'
import Confetti from 'react-confetti-boom'

interface WinnerCardProps {
  winner?: string
  dummyUsers?: string[]
  startAnimation?: boolean
  delay?: number
  onWinnerRevealed?: () => void
}

export default function WinnerCard({
  winner = 'WINNER123',
  dummyUsers = [
    'USER001',
    'USER002',
    'USER003',
    'USER004',
    'USER005',
    'USER006',
    'USER007',
    'USER008',
    'USER009',
    'USER010',
    'USER011',
    'USER012',
    'USER013',
    'USER014',
    'USER015',
    'USER016',
    'USER017',
    'USER018',
    'USER019',
  ],
  startAnimation = false,
  delay = 0,
  onWinnerRevealed,
}: WinnerCardProps) {
  const scrollRef = React.useRef<HTMLDivElement>(null)
  const [showConfetti, setShowConfetti] = React.useState(false)

  // Initialize scroll position to show only dummy users (hide winner initially)
  React.useEffect(() => {
    if (scrollRef.current && !startAnimation) {
      scrollRef.current.scrollTop = 0
    }
  }, [startAnimation])

  React.useEffect(() => {
    if (startAnimation && scrollRef.current) {
      const scrollContainer = scrollRef.current
      const itemHeight = 105 // Height of each agent-code item

      // Start scrolling with slower speed for 5 second duration
      let currentScrollTop = 0
      let speed = 8 // Pixels per frame (reduced for slower animation)
      let frameCount = 0
      const totalFrames = 300 // Total animation frames (5 seconds at 60fps)

      const animateScroll = () => {
        frameCount++

        // Calculate scroll position - scroll through multiple cycles
        const cyclesToShow = 3 // Show 3 full cycles before slowing down
        const totalScrollDistance = dummyUsers.length * itemHeight * cyclesToShow + dummyUsers.length * itemHeight

        if (frameCount < totalFrames * 0.8) {
          // Fast scrolling phase (80% of animation - 4 seconds)
          currentScrollTop += speed
          speed = Math.min(speed * 1.008, 15) // Gradually increase speed more slowly
        } else {
          // Slow down phase (last 20% of animation - 1 second)
          speed = Math.max(speed * 0.92, 1) // Gradually decrease speed more slowly
          currentScrollTop += speed
        }

        // Apply the scroll with modulo to create cycling effect
        scrollContainer.scrollTop = currentScrollTop % (dummyUsers.length * itemHeight)

        if (frameCount < totalFrames) {
          requestAnimationFrame(animateScroll)
        } else {
          // Final smooth scroll to winner
          const winnerPosition = dummyUsers.length * itemHeight
          scrollContainer.scrollTo({
            top: winnerPosition,
            behavior: 'smooth',
          })

          // Trigger confetti after smooth scroll completes
          setTimeout(() => {
            setShowConfetti(true)
            onWinnerRevealed?.()
          }, 800) // Wait for smooth scroll to complete
        }
      }

      // Start animation with stagger delay
      setTimeout(() => {
        requestAnimationFrame(animateScroll)
      }, delay)
    }
  }, [startAnimation, dummyUsers.length, delay])
  return (
    <CardContainer>
      {showConfetti && (
        <Confetti
          mode="boom"
          particleCount={200}
          spreadDeg={180}
          shapeSize={12}
          colors={['#ff6b6b', '#4ecdc4', '#45b7d1', '#f9ca24', '#f0932b', '#eb4d4b', '#6c5ce7', '#a55eea']}
        />
      )}
      <Item ref={scrollRef}>
        {dummyUsers.map((user, index) => (
          <div key={index} className="agent-code">
            {user}
          </div>
        ))}
        <div className="agent-code winner">{winner}</div>
      </Item>
    </CardContainer>
  )
}

const CardContainer = styled.div`
  position: relative;
  width: 100%;
`

export const Item = styled.div`
  width: 100%;
  max-width: 266px;
  display: flex;
  height: 105px;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  background: rgba(0, 0, 0, 0.7);
  overflow-x: hidden;
  overflow-y: scroll;
  scroll-behavior: smooth;
  scroll-snap-type: y mandatory;

  &::-webkit-scrollbar {
    width: 8px;
  }

  .agent-code {
    color: var(--White-01, #fff);
    text-align: center;
    font-family: 'FS Elliot Pro';
    font-size: clamp(2.5rem, 1.5rem + 1vw, 4rem);
    font-style: normal;
    font-weight: 900;
    line-height: normal;
    text-transform: uppercase;
    align-self: stretch;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 105px;
    scroll-snap-align: center;

    &.winner {
      color: #fff;
      font-weight: 900;
    }
  }
`
